import { useToast } from "@/hooks/useToast";
import { supabase } from "@/lib/supabase";
import { notifyWorkspaceMembersAboutThread } from "@/lib/utils/notificationUtils";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { useThreadStore } from "@/store/index";
import useAuthStore from "@/store/authStore";
import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

export function useProjectActions({
  workspaces,
  currentWorkspaceId,
  setCurrentWorkspaceId,
  setCurrentWorkspace,
  setWorkspaces,
  expandedWorkspaces,
  setExpandedWorkspaces,
  workspaceThreads,
  setWorkspaceThreads,
  markThreadAsNew,
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const {
    createThread,
    setCurrentThread,
    setCurrentThreadId,
    clearCurrentThread,
  } = useThreadStore();

  const [creatingChatId, setCreatingChatId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Workspace rename dialog state
  const [workspaceRenameDialogOpen, setWorkspaceRenameDialogOpen] =
    useState(false);
  const [workspaceToRename, setWorkspaceToRename] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isRenamingWorkspace, setIsRenamingWorkspace] = useState(false);

  // Workspace delete dialog state
  const [workspaceDeleteDialogOpen, setWorkspaceDeleteDialogOpen] =
    useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState(null);

  const handleWorkspaceSelect = useCallback(
    (workspaceId) => {
      const workspace = workspaces.find((w) => w.id === workspaceId);

      setCurrentWorkspaceId(workspaceId);
      setCurrentWorkspace(workspace);

      // Preserve existing expansion state and only expand the selected workspace
      // This allows multiple workspaces to be open simultaneously
      setExpandedWorkspaces((prev) => ({
        ...prev,
        [workspaceId]: true, // Always expand the selected workspace
      }));

      clearCurrentThread();

      localStorage.removeItem("threadSelectedFromSidebar");
      localStorage.removeItem("threadSelectionTimestamp");
      localStorage.removeItem("workspaceThreadContext");
      localStorage.removeItem("lastVisitedThreadId");
      localStorage.removeItem("pendingWorkspaceChat");
      localStorage.removeItem("newlyCreatedThreadId");

      localStorage.setItem("intentionalProjectNavigation", "true");

      // Check if we're coming from a chat page - if so, use replace to avoid page reload
      const isFromChatPage = location.pathname.startsWith("/chat/");
      const navigateOptions = isFromChatPage
        ? { replace: true, state: { preventReload: true } }
        : { state: { preventReload: true } };

      navigate(`/projects/${workspaceId}`, navigateOptions);
    },
    [
      workspaces,
      setCurrentWorkspaceId,
      setCurrentWorkspace,
      setExpandedWorkspaces,
      clearCurrentThread,
      navigate,
      location.pathname,
    ]
  );

  const handleCreateChat = useCallback(
    async (e, workspaceId) => {
      e.stopPropagation();

      try {
        setCreatingChatId(workspaceId);

        const workspace = workspaces.find((w) => w.id === workspaceId);
        if (!workspace) {
          throw new Error("Project not found");
        }

        clearCurrentThread();

        localStorage.setItem("pendingWorkspaceChat", workspaceId);
        localStorage.setItem(
          "pendingWorkspaceType",
          workspace.is_team ? "team" : "personal"
        );
        localStorage.setItem("createSupabaseOnlyThread", "true");
        localStorage.setItem("forceNewThread", "true");
        localStorage.setItem("clearMessagesAndContext", "true");
        localStorage.setItem("needs_title_generation", "true");

        const { data: membership, error: membershipError } = await supabase
          .from("workspace_members")
          .select("role, permissions")
          .eq("workspace_id", workspaceId)
          .eq("user_id", user.id)
          .single();

        if (membershipError) {
          throw new Error("Failed to verify project permissions");
        }

        if (!membership) {
          throw new Error("You are not a member of this project");
        }

        const canCreateThreads =
          membership.role === "owner" ||
          membership.permissions?.workspace?.invite_members === true;

        if (!canCreateThreads) {
          throw new Error(
            "You don't have permission to create chats in this project"
          );
        }

        const userId = String(user.id || "");
        const workspaceIdStr = String(workspaceId || "");

        const threadMetadata = {
          user_id: userId,
          workspace_id: workspaceIdStr,
          created_at: new Date().toISOString(),
          workspace_name: workspace?.name || "Project",
          is_team: workspace.is_team === true,
          is_shared_thread: workspace.is_team === true,
          shared_in_workspace:
            workspace.is_team === true ? workspaceIdStr : null,
        };

        let threadTitle;
        try {
          const pendingContent = localStorage.getItem(
            "pendingWorkspaceMessage"
          );
          if (pendingContent) {
            const { generateThreadTitle } = await import(
              "@/contexts/ChatContext"
            );
            const generatedTitle = await generateThreadTitle(pendingContent);
            if (generatedTitle) {
              threadTitle = generatedTitle;
              localStorage.setItem("workspace_generated_title", generatedTitle);
              localStorage.setItem("workspace_title_generated", "true");
            }
          }
        } catch (titleError) {
          console.error("Error generating title in sidebar:", titleError);
        }

        const finalTitle =
          threadTitle ||
          `AI Chat ${new Date().toLocaleTimeString().replace(/:/g, "-")}`;

        const thread = await createThread(
          userId,
          null,
          finalTitle,
          "",
          threadMetadata
        );

        if (!thread || !thread.id) {
          throw new Error("Failed to create thread");
        }

        await SupabaseWorkspaceUtils.addThreadToWorkspace(
          workspaceIdStr,
          String(thread.id)
        );

        await notifyWorkspaceMembersAboutThread(
          thread.id,
          workspaceIdStr,
          userId,
          thread.title || `Chat with ${workspace.name}`
        );

        const updatedWorkspace =
          await SupabaseWorkspaceUtils.getWorkspace(workspaceIdStr);

        setWorkspaces(
          workspaces.map((w) => (w.id === workspaceId ? updatedWorkspace : w))
        );

        setWorkspaceThreads((prev) => {
          const updatedThreads = [...(prev[workspaceId] || [])];
          const newThread = {
            id: thread.id,
            title: thread.title || `Chat ${thread.id.substring(0, 6)}...`,
            status: "active",
          };
          updatedThreads.push(newThread);

          if (markThreadAsNew) {
            markThreadAsNew(thread.id, newThread.title);
          }

          return {
            ...prev,
            [workspaceId]: updatedThreads,
          };
        });

        setExpandedWorkspaces((prev) => ({
          ...prev,
          [workspaceId]: true,
        }));

        localStorage.setItem("newlyCreatedThreadId", thread.id);

        setCurrentThread(thread);
        setCurrentThreadId(thread.id);

        // Check if we're coming from a project page - if so, use replace to avoid page reload
        const isFromProjectPage = location.pathname.startsWith("/projects/");
        const navigateOptions = isFromProjectPage
          ? { replace: true, state: { preventReload: true } }
          : { state: { preventReload: true } };
        navigate(`/chat/${thread.id}`, navigateOptions);
      } catch (error) {
        console.error("Error creating chat with project:", error);
      } finally {
        setCreatingChatId(null);
      }
    },
    [
      workspaces,
      user,
      createThread,
      setWorkspaces,
      setWorkspaceThreads,
      setExpandedWorkspaces,
      markThreadAsNew,
      setCurrentThread,
      setCurrentThreadId,
      clearCurrentThread,
      navigate,
      location.pathname,
    ]
  );

  const handleWorkspaceRename = useCallback((e, workspace) => {
    e.stopPropagation();
    setWorkspaceToRename(workspace);
    setNewWorkspaceName(workspace.name || "");
    setWorkspaceRenameDialogOpen(true);
  }, []);

  const handleWorkspaceRenameSubmit = useCallback(async () => {
    if (!workspaceToRename || !newWorkspaceName.trim()) return;

    try {
      setIsRenamingWorkspace(true);

      const trimmedName = newWorkspaceName.trim();

      // Perform the update without .single() to avoid RLS issues
      const { data: updatedWorkspace, error } = await supabase
        .from("workspaces")
        .update({
          name: trimmedName,
          updated_at: new Date().toISOString(),
        })
        .eq("id", workspaceToRename.id)
        .select("*");

      // Check if update succeeded (even if RLS prevents reading the row back)
      // PGRST116 means the update succeeded but we can't read the row due to RLS
      if (error && error.code !== "PGRST116") {
        console.error("Error updating workspace name:", error);
        toast({
          title: "Error",
          description: "Failed to rename project. Please try again.",
          variant: "destructive",
        });
        throw error;
      }

      // Update state with the new name
      // If updatedWorkspace is empty due to RLS, we'll use the existing workspace data
      const updatedWorkspaceData =
        updatedWorkspace && updatedWorkspace.length > 0
          ? updatedWorkspace[0]
          : {
              ...workspaceToRename,
              name: trimmedName,
              updated_at: new Date().toISOString(),
            };

      setWorkspaces(
        workspaces.map((w) =>
          w.id === workspaceToRename.id
            ? {
                ...w,
                name: trimmedName,
                updated_at: updatedWorkspaceData.updated_at,
              }
            : w
        )
      );

      if (currentWorkspaceId === workspaceToRename.id) {
        setCurrentWorkspace({
          ...workspaceToRename,
          name: trimmedName,
          updated_at: updatedWorkspaceData.updated_at,
        });
      }

      toast({
        title: "Success",
        description: "Project renamed successfully.",
      });

      setWorkspaceRenameDialogOpen(false);
      setWorkspaceToRename(null);
      setNewWorkspaceName("");
    } catch (error) {
      console.error("Error renaming workspace:", error);
      // Error toast is already shown above for non-PGRST116 errors
    } finally {
      setIsRenamingWorkspace(false);
    }
  }, [
    workspaceToRename,
    newWorkspaceName,
    workspaces,
    currentWorkspaceId,
    setWorkspaces,
    setCurrentWorkspace,
    toast,
  ]);

  const handleWorkspaceRenameCancel = useCallback(() => {
    setWorkspaceRenameDialogOpen(false);
    setWorkspaceToRename(null);
    setNewWorkspaceName("");
  }, []);

  const handleWorkspaceDelete = useCallback(
    (e, workspaceId) => {
      e.stopPropagation();

      if (workspaces.length <= 1) {
        toast({
          title: "Cannot Delete Project",
          description:
            "You cannot delete your last project. You must have at least one project.",
          variant: "destructive",
        });
        return;
      }

      const workspace = workspaces.find((w) => w.id === workspaceId);

      if (workspace && !workspace.is_team) {
        const personalWorkspaces = workspaces.filter((w) => !w.is_team);
        if (personalWorkspaces.length <= 1) {
          toast({
            title: "Cannot Delete Project",
            description:
              "You cannot remove your last personal project. You must have at least one personal project.",
            variant: "destructive",
          });
          return;
        }
      }

      setWorkspaceToDelete(workspace);
      setWorkspaceDeleteDialogOpen(true);
    },
    [workspaces, toast]
  );

  const handleConfirmWorkspaceDelete = useCallback(async () => {
    if (!workspaceToDelete) return;

    try {
      setIsLoading(true);

      if (workspaceToDelete && workspaceToDelete.vector_store_id) {
        try {
          const { deleteVectorStore } = await import(
            "@/lib/utils/vectorStoreUtils"
          );
          await deleteVectorStore(workspaceToDelete.vector_store_id);
        } catch (vectorStoreError) {
          // Log error but continue with workspace deletion
          // Vector store deletion failure shouldn't block workspace deletion
          console.warn(
            "Could not delete vector store (may not exist), continuing with workspace deletion:",
            vectorStoreError.message || vectorStoreError
          );
        }
      }

      const { error } = await SupabaseWorkspaceUtils.deleteWorkspace(
        workspaceToDelete.id
      );

      if (error) {
        throw error;
      }

      setWorkspaces(workspaces.filter((w) => w.id !== workspaceToDelete.id));

      if (currentWorkspaceId === workspaceToDelete.id) {
        navigate("/chat");
      }

      setWorkspaceDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
    } catch (error) {
      console.error("Error deleting project:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    workspaceToDelete,
    workspaces,
    currentWorkspaceId,
    setWorkspaces,
    navigate,
  ]);

  const handleCancelWorkspaceDelete = useCallback(() => {
    setWorkspaceDeleteDialogOpen(false);
    setWorkspaceToDelete(null);
  }, []);

  /**
   * Update workspace color and/or icon
   * @param {string} workspaceId - The workspace ID to customize
   * @param {Object} customization - The customization options
   * @param {string} [customization.color] - The color hex value
   * @param {string} [customization.icon] - The icon value
   */
  const handleWorkspaceCustomize = useCallback(
    async (workspaceId, { color, icon }) => {
      const workspace = workspaces.find((w) => w.id === workspaceId);
      if (!workspace) return;

      try {
        // Merge with existing metadata
        const updatedMetadata = {
          ...(workspace.metadata || {}),
          ...(color !== undefined && { color }),
          ...(icon !== undefined && { icon }),
        };

        const { data: updatedWorkspace, error } = await supabase
          .from("workspaces")
          .update({
            metadata: updatedMetadata,
            updated_at: new Date().toISOString(),
          })
          .eq("id", workspaceId)
          .select("*");

        if (error && error.code !== "PGRST116") {
          console.error("Error updating workspace customization:", error);
          toast({
            title: "Error",
            description: "Failed to update project appearance. Please try again.",
            variant: "destructive",
          });
          throw error;
        }

        // Update local state
        const updatedWorkspaceData =
          updatedWorkspace && updatedWorkspace.length > 0
            ? updatedWorkspace[0]
            : {
                ...workspace,
                metadata: updatedMetadata,
                updated_at: new Date().toISOString(),
              };

        setWorkspaces(
          workspaces.map((w) =>
            w.id === workspaceId
              ? {
                  ...w,
                  metadata: updatedMetadata,
                  updated_at: updatedWorkspaceData.updated_at,
                }
              : w
          )
        );

        if (currentWorkspaceId === workspaceId) {
          setCurrentWorkspace({
            ...workspace,
            metadata: updatedMetadata,
            updated_at: updatedWorkspaceData.updated_at,
          });
        }
      } catch (error) {
        console.error("Error customizing workspace:", error);
      }
    },
    [workspaces, currentWorkspaceId, setWorkspaces, setCurrentWorkspace, toast]
  );

  return {
    // State
    creatingChatId,
    isLoading,
    workspaceRenameDialogOpen,
    workspaceToRename,
    newWorkspaceName,
    isRenamingWorkspace,
    workspaceDeleteDialogOpen,
    workspaceToDelete,

    // Setters
    setNewWorkspaceName,

    // Handlers
    handleWorkspaceSelect,
    handleCreateChat,
    handleWorkspaceRename,
    handleWorkspaceRenameSubmit,
    handleWorkspaceRenameCancel,
    handleWorkspaceDelete,
    handleConfirmWorkspaceDelete,
    handleCancelWorkspaceDelete,
    handleWorkspaceCustomize,
  };
}
